import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Package, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export const FloatingTrackButton = () => {
  const [isTrackingDialogOpen, setIsTrackingDialogOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Small delay before showing the button for better effect
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const handleTrackSubmit = () => {
    if (trackingNumber.trim()) {
      void navigate(`/tracking/${trackingNumber.trim()}`);
      setTrackingNumber("");
      setIsTrackingDialogOpen(false);
    }
  };

  return (
    <>
      <div
        className={`fixed bottom-6 right-6 z-20 transition-all duration-500 transform ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <Button
          size="lg"
          className="rounded-full flex items-center gap-3 shadow-xl bg-white text-gray-800 hover:bg-gray-100 px-6 py-5 transition-all duration-300 border border-gray-200 hover:scale-105"
          onClick={() => setIsTrackingDialogOpen(true)}
        >
          <HelpCircle className="h-7 w-7 text-purple-600" />
          <span className="font-medium text-md">Track your package?</span>
        </Button>
      </div>

      {/* Tracking Dialog */}
      <Dialog
        open={isTrackingDialogOpen}
        onOpenChange={setIsTrackingDialogOpen}
      >
        <DialogContent className="w-[90%] max-w-md mx-auto p-4 sm:p-6 rounded-xl">
          <DialogHeader className="mb-2">
            <DialogTitle className="flex items-center justify-center sm:justify-start gap-2 text-lg sm:text-xl">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
              Track Your Package
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-center sm:text-left">
              Enter your tracking number to get real-time updates
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2 sm:py-4">
            <div className="relative">
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
                className="w-full px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base sm:text-lg"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleTrackSubmit();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex flex-col sm:flex-row w-full gap-2 sm:justify-end mt-2">
              <Button
                variant="outline"
                onClick={() => setIsTrackingDialogOpen(false)}
                className="w-full sm:w-auto text-sm sm:text-base py-2 order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleTrackSubmit}
                className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-sm sm:text-base py-2 order-1 sm:order-2"
              >
                Track
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
